/* eslint-disable import/prefer-default-export,import/named,no-continue,no-underscore-dangle,no-nested-ternary,max-len,no-debugger */
import {
  ILotLine, TLotLineType, TTuplesSideId, TLotType, TSidePerId, TSideLine,
} from '../../types';
import {
  FRONT_LOT_LINE, REAR_LOT_LINE, SIDE_LOT_LINE, TOP_LEFT_POINT,
  LOT,
} from '../../constants';
import { Counter } from '../general/counter';
import { ILine, IPoint } from '../../types/ark1.0/types';
import { equalPoint, move as movePoint } from '../point';
import { getCenter, getPoints, getPointSide } from '../line';
import {
  findIndex, idFilter, splice, toInches,
} from '../general';

const lotTypeSchema = {
  [LOT.TYPES.CORNER_LOT]: [SIDE_LOT_LINE, SIDE_LOT_LINE, FRONT_LOT_LINE, FRONT_LOT_LINE],
  [LOT.TYPES.TWO_CORNER_LOT]: [SIDE_LOT_LINE, FRONT_LOT_LINE, FRONT_LOT_LINE, FRONT_LOT_LINE],
  [LOT.TYPES.INFILL_LOT]: [REAR_LOT_LINE, SIDE_LOT_LINE, FRONT_LOT_LINE, SIDE_LOT_LINE],
  [LOT.TYPES.THROUGH_LOT]: [FRONT_LOT_LINE, SIDE_LOT_LINE, FRONT_LOT_LINE, SIDE_LOT_LINE],
  [LOT.TYPES.ENTIRE_BLOCK]: [FRONT_LOT_LINE, FRONT_LOT_LINE, FRONT_LOT_LINE, FRONT_LOT_LINE],
  [LOT.TYPES.IRREGULAR]: [],
};

export const buildDefaultLotLines = (
  height: number,
  width: number,
  nextId = 1,
  lotType: TLotType = LOT.TYPES.INFILL_LOT as TLotType,
): ILotLine[] => {
  const counter = new Counter(nextId);
  const p1 = { ...TOP_LEFT_POINT };
  const p2 = { ...p1, x: p1.x + toInches(width) };
  const p3 = { ...p2, y: p2.y + toInches(height) };
  const p4 = { ...p3, x: p1.x };
  return [p1, p2, p3, p4, p1]
    .reduce((acc: ILotLine[], item: IPoint, index, list: IPoint[]) => {
      if (index === list.length - 1) {
        return acc;
      }
      const line: ILotLine = {
        id: counter.get(),
        type: lotTypeSchema[lotType][index] as unknown as TLotLineType,
        startPoint: { ...item },
        endPoint: { ...list[index + 1] },
        streetType: lotTypeSchema[lotType][index] === FRONT_LOT_LINE ? 'NARROW' : undefined,
      };
      return [...acc, line];
    }, []);
};

export const move = (line: ILotLine, delta: IPoint): ILotLine => ({
  ...line, startPoint: movePoint(line.startPoint, delta), endPoint: movePoint(line.endPoint, delta),
});

export const moveLinePoint = (line: ILotLine, delta: IPoint, side: 'startPoint' | 'endPoint'): ILotLine => ({
  ...line, [side]: movePoint(line[side], delta),
});

export const getAdjacentLines = (line: ILotLine, lines: ILotLine[]): ILotLine[] => {
  debugger;
  const [p0, p1] = getPoints(line);
  const result: ILotLine[] = [];
  for (let i = 0; (i < lines.length && result.length < 2); i += 1) {
    if (lines[i].id === line.id) {
      continue;
    }
    if (equalPoint(lines[i].endPoint, p0)) {
      result.unshift(lines[i]);
    }
    if (equalPoint(lines[i].startPoint, p1)) {
      result.push(lines[i]);
    }
  }
  return result;
};

export const getBoundedSidePerLine = (line: ILotLine, lines: ILotLine[]): TTuplesSideId[] => {
  const [p0, p1] = getPoints(line);
  const result: TTuplesSideId[] = [];
  for (let i = 0; (i < lines.length && result.length < 2); i += 1) {
    if (lines[i].id === line.id) {
      continue;
    }
    if (equalPoint(lines[i].endPoint, p0)) {
      result.push(['endPoint', lines[i].id]);
    }
    if (equalPoint(lines[i].startPoint, p1)) {
      result.push(['startPoint', lines[i].id]);
    }
  }
  return result;
};

const _getId = (item: [('startPoint' | 'endPoint'), number]): number => item[1];
const _getSide = (item: [('startPoint' | 'endPoint'), number]): ('startPoint' | 'endPoint') => item[0];

export const getSidePerLineId = (point: IPoint, lines: ILotLine[]): TSidePerId => (
  lines.reduce((acc: TSidePerId, line) => {
    const side = getPointSide(point, line);
    return side ? { ...acc, [side]: line.id } : acc;
  }, {} as TSidePerId));

export const moveLotLines = (lines: ILotLine[], delta: IPoint, listSidePerId: TTuplesSideId[]): ILotLine[] => lines
  .map((lotLine) => {
    const sidePerIdFiltered = listSidePerId.filter((item) => _getId(item) === lotLine.id) as [('startPoint' | 'endPoint'), number][];
    const sides = sidePerIdFiltered.map((spi) => _getSide(spi));
    return sides.length > 0
      ? sides.reduce((acc, side) => moveLinePoint(acc, delta, side), lotLine)
      : lotLine;
  }) as ILotLine[];

export const moveLotLine = (lines: ILotLine[], delta: IPoint, sidePerId: TSidePerId): ILotLine[] => (
  moveLotLines(lines, delta, (Object.entries(sidePerId) as [TSideLine, number][]))
);

export const splitLotLine = (line: ILotLine, counter: Counter): ILotLine[] => {
  const [p0, p1] = getPoints(line);
  const center = getCenter(line);
  return [
    {
      ...line, startPoint: p0, endPoint: { ...center }, id: counter.get(),
    },
    {
      ...line, startPoint: { ...center }, endPoint: p1, id: counter.get(),
    },
  ];
};

export const getBoundedLines = <T extends ILine>(point: IPoint, lines: T[]): T[] => {
  const [line1, line2] = lines.reduce((acc: T[], line) => {
    const [p0, p1] = getPoints(line);
    if (equalPoint(p0, point)) {
      return [...acc, line];
    }
    if (equalPoint(p1, point)) {
      return [line, ...acc];
    }
    return acc;
  }, []);
  return [line1, line2];
};

export const removeVertex = (
  point: IPoint,
  lines: ILotLine[],
  typeLine?: TLotLineType,
): ILotLine[] => {
  const [line1, line2] = getBoundedLines(point, lines);
  const index = findIndex(line1, lines, idFilter);
  const line = {
    id: line1.id, type: typeLine || line1.type, startPoint: line1.startPoint, endPoint: line2.endPoint,
  };
  return splice(lines, { index, qty: 2 }, line);
};
